/**
 * The station layers of the MapLibre weather map (#336 MR2b), as plain
 * style-spec objects like `referenceLayerSpecs`: MapLibre Native takes them
 * as they are (mobile's `StationsLayer`, step 2 MR6), over a GeoJSON source
 * of `stationMarkerFeatures`. They carry no anchor: the stations draw on top
 * of everything, as the react-native-maps markers did.
 *
 * One badge per metric, matching mobile's former SVG markers
 * (`components/MapSymbol/*`, removed with react-native-maps in 1.65):
 *
 * - temperature: a `circle` (no sprite) of radius 15 with a 3px white ring
 *   -- MapLibre strokes OUTSIDE the radius, so 15 + 3 = the SVG's 18 outer
 *   radius **[unverified on Native: check on device]** -- and the value on
 *   it, 13px bold, in the feature's `ink`;
 * - rain: the `drop` sprite tinted by an SDF `icon-color`, a white halo, the
 *   value 11px bold just below its centre (the bulb). `icon-halo-width` only
 *   draws as far as the SDF's encoded distance reaches, so the sprite script
 *   must leave room for it **[unverified: tune on device in MR6]**; the
 *   legacy SVG stroke is 2.5 centred, about 1.25px outside the shape;
 * - gust: the `arrow` sprite, rotated with the map, no text.
 *
 * Every property MapLibre types as an array is written as an array: Native
 * Android crashes on a scalar where the style-spec allows one
 * (`nativeSafety.ts`), and a style-spec-invalid expression drops the whole
 * layer there, so a core test validates every spec against the style-spec.
 *
 * `text-allow-overlap`/`icon-allow-overlap` keep today's draw-everything
 * behaviour: the backend already spaces its clusters (so a label can overlap
 * a neighbouring badge, as the SVG markers can today). The temperature
 * label is Noto Sans Bold (700), the closest face positron's glyph server
 * has to the legacy SVG's 600.
 */
import type {StationMarkerMetric} from './stationMarker';

/** The GeoJSON source the layers read, unless a caller names another. */
export const STATION_MARKERS_SOURCE_ID = 'sm-stations';

/**
 * The badge font, from positron's glyph server (which carries Noto Sans
 * Regular, Bold and Italic). A font STACK: an array, never a string.
 */
export const STATION_BADGE_FONT: readonly string[] = ['Noto Sans Bold'];

/** The white ring and halo every badge sits in. */
export const STATION_BADGE_RING_COLOR = '#ffffff';

export type StationBadgeSprite = {
  /** SVG path, in a `width` x `height` user-space box. */
  path: string;
  width: number;
  height: number;
  /** White outline width of the legacy SVG marker, in the same units. */
  stroke: number;
};

/**
 * The two sprite badges' geometry, for mobile's build-time sprite script
 * (and its drift test). Copied from the legacy markers; since they went with
 * the react-native-maps weather map (1.65), this is the only copy.
 */
export const STATION_BADGE_SPRITES: Readonly<
  Record<'drop' | 'arrow', Readonly<StationBadgeSprite>>
> = Object.freeze({
  // Water drop: pointed top at (14,2), circular bottom centred at (14,22) r=11.
  drop: {
    path: 'M14 2 Q14 2 25 22 A11 11 0 1 1 3 22 Q14 2 14 2 Z',
    width: 28,
    height: 36,
    stroke: 2.5,
  },
  // Navigation arrow, pointing up (north at rotation 0): top point, right
  // wing, inner notch, left wing.
  arrow: {
    path: 'M15 2 L26 28 L15 20 L4 28 Z',
    width: 30,
    height: 30,
    stroke: 2.5,
  },
});

/**
 * The prefix of the badge images' ids in the map's style. Never a bare
 * `drop`/`arrow`: OpenFreeMap positron's own sprite has an `arrow` (and a
 * `circle`), and under that name the gust layer drew positron's small
 * one-way arrow instead of the badge (Android emulator, #336 MR6).
 */
export const STATION_BADGE_IMAGE_PREFIX = 'sm-station-';

/** The style image id of a badge sprite: what the app registers it under. */
export const stationBadgeImageId = (icon: 'drop' | 'arrow'): string =>
  `${STATION_BADGE_IMAGE_PREFIX}${icon}`;

export type StationLayerSpec = {
  id: string;
  type: 'circle' | 'symbol';
  source: string;
  layout?: Record<string, unknown>;
  paint: Record<string, unknown>;
};

const label = (size: number, offset?: [number, number]) => ({
  'text-field': ['get', 'label'],
  'text-font': [...STATION_BADGE_FONT],
  'text-size': size,
  ...(offset ? {'text-offset': [...offset]} : {}),
  'text-allow-overlap': true,
  'text-ignore-placement': true,
});

const sprite = () => ({
  // `stationBadgeImageId` of the feature's `icon`.
  'icon-image': ['concat', STATION_BADGE_IMAGE_PREFIX, ['get', 'icon']],
  'icon-allow-overlap': true,
  'icon-ignore-placement': true,
});

const spriteColours = () => ({
  'icon-color': ['get', 'color'],
  'icon-halo-color': STATION_BADGE_RING_COLOR,
  'icon-halo-width': 2.5,
});

/**
 * The layers that draw the stations for `metric`, bottom first. Ids are
 * per metric (`sm-stations-<metric>-…`), so a metric switch replaces the
 * layers while the source stays. A new object every call.
 */
export const stationLayerSpecs = (
  metric: StationMarkerMetric,
  {source = STATION_MARKERS_SOURCE_ID}: {source?: string} = {},
): StationLayerSpec[] => {
  switch (metric) {
    case 'RR_1H':
      return [
        {
          id: 'sm-stations-rain',
          type: 'symbol',
          source,
          layout: {...sprite(), ...label(11, [0, 0.3])},
          paint: {...spriteColours(), 'text-color': ['get', 'ink']},
        },
      ];
    case 'FXY':
      return [
        {
          id: 'sm-stations-wind',
          type: 'symbol',
          source,
          layout: {
            ...sprite(),
            'icon-rotate': ['get', 'rotation'],
            'icon-rotation-alignment': 'map',
          },
          paint: spriteColours(),
        },
      ];
    case 'T':
    default:
      return [
        {
          id: 'sm-stations-temperature-badge',
          type: 'circle',
          source,
          paint: {
            'circle-radius': 15,
            'circle-color': ['get', 'color'],
            'circle-stroke-color': STATION_BADGE_RING_COLOR,
            'circle-stroke-width': 3,
          },
        },
        {
          id: 'sm-stations-temperature-label',
          type: 'symbol',
          source,
          layout: label(13),
          paint: {'text-color': ['get', 'ink']},
        },
      ];
  }
};
